import { formatDateTime } from './date-format'

export type DeadlineTone = 'overdue' | 'today' | 'future' | 'none' | 'done'

export interface DeadlineInfo {
  tone: DeadlineTone
  /** Texto do badge: "Vencido há 75d", "Vence hoje", "Em 3 dias", "Sem prazo". */
  short: string
  /** Versão por extenso (card do cabeçalho da conversa): "Vencido há 26 dias". */
  long: string
  /** Data do prazo para a linha de baixo: "31/07, 08:59" (ou "31/07" sem hora). */
  date: string
}

const HOUR_MS = 60 * 60 * 1000
const DAY_MS = 24 * HOUR_MS
/** Prazo gravado só com a data (`YYYY-MM-DD`), sem hora. */
export const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/

/** Prazo só com data vale até o fim do dia LOCAL (não meia-noite UTC, que no
 * Brasil cairia na noite anterior). */
export function parsePrazo(prazo: string): Date | null {
  if (DATE_ONLY.test(prazo)) {
    const [y, m, d] = prazo.split('-').map(Number)
    return new Date(y, m - 1, d, 23, 59, 59)
  }
  const date = new Date(prazo)
  return Number.isNaN(date.getTime()) ? null : date
}

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
}

function formatPrazoDate(prazo: string): string {
  if (DATE_ONLY.test(prazo)) {
    const [, m, d] = prazo.split('-')
    return `${d}/${m}`
  }
  return formatDateTime(prazo)
}

const plural = (n: number, um: string, varios: string) =>
  `${n} ${n === 1 ? um : varios}`

/** Há quanto tempo venceu: dias inteiros, ou horas no primeiro dia. */
function overdueAmount(due: Date, now: Date): { n: number; unit: 'd' | 'h' } | null {
  const diff = now.getTime() - due.getTime()
  if (diff <= 0) return null
  const days = Math.floor(diff / DAY_MS)
  if (days >= 1) return { n: days, unit: 'd' }
  return { n: Math.max(1, Math.floor(diff / HOUR_MS)), unit: 'h' }
}

const amountText = (a: { n: number; unit: 'd' | 'h' }) =>
  a.unit === 'd' ? plural(a.n, 'dia', 'dias') : plural(a.n, 'hora', 'horas')

/** "3 dias" / "5 horas" desde que o prazo venceu; `null` se não venceu. */
export function overdueFor(prazo: string | null | undefined, now: Date = new Date()): string | null {
  const due = prazo ? parsePrazo(prazo) : null
  const amount = due ? overdueAmount(due, now) : null
  return amount ? amountText(amount) : null
}

/**
 * Situação do prazo de uma ocorrência, nos textos das telas de Ocorrências
 * ("Vencido há 75d", "Vence hoje", "Em 3 dias", "Sem prazo"). Finalizada não
 * vence mais: só mostra a data.
 */
export function describeDeadline(
  prazo: string | null | undefined,
  isDone: boolean,
  now: Date = new Date(),
): DeadlineInfo {
  const due = prazo ? parsePrazo(prazo) : null
  if (!prazo || !due) {
    return {
      tone: 'none',
      short: 'Sem prazo',
      long: 'Sem prazo definido',
      date: '',
    }
  }
  const date = formatPrazoDate(prazo)
  if (isDone) return { tone: 'done', short: date, long: date, date }

  const overdue = overdueAmount(due, now)
  if (overdue) {
    return {
      tone: 'overdue',
      short: `Vencido há ${overdue.n}${overdue.unit}`,
      long: `Vencido há ${amountText(overdue)}`,
      date,
    }
  }

  const calendarDays = Math.round((startOfDay(due) - startOfDay(now)) / DAY_MS)
  if (calendarDays <= 0) {
    return { tone: 'today', short: 'Vence hoje', long: 'Vence hoje', date }
  }
  const text = calendarDays === 1 ? 'Amanhã' : `Em ${calendarDays} dias`
  return { tone: 'future', short: text, long: text, date }
}
